import React from 'react';
import classNames from 'classnames';
import { Form, Table } from 'react-bootstrap';
import FormattedCurrency from '../../../../../components/currency/FormattedCurrency';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { applyMeritPercent, meritFromAmounts } from '../logic';
import { CompensationReviewItem } from '../types';
import { PayBandCell } from './PayBandCell';

type Props = {
  items: CompensationReviewItem[];
  currency: string;
  // When false the grid is fully read-only (approver view / submitted+ status).
  editable: boolean;
  // The Include column is hidden once a review has been submitted.
  showIncludeColumn: boolean;
  onNewAmountChange: (itemId: string, newAmount: number) => void;
  onIncludedChange: (itemId: string, included: boolean) => void;
};

const frequencyKey = (item: CompensationReviewItem): string =>
  `frequencies.${item.frequency.toLowerCase()}`;

export const CompensationReviewItemsTable: React.FC<Props> = ({
  items,
  currency,
  editable,
  showIncludeColumn,
  onNewAmountChange,
  onIncludedChange,
}) => {
  const t = useNamespacedTranslation('compensationReviews.table');

  return (
    <Table responsive hover className="card-table table-nowrap align-middle">
      <thead>
        <tr>
          <th>{t('employee')}</th>
          <th>{t('position')}</th>
          <th>{t('frequency')}</th>
          <th className="text-end">{t('reference')}</th>
          <th className="text-end">{t('meritPercent')}</th>
          <th className="text-end compensation-new-amount">{t('newAmount')}</th>
          <th>{t('payBand')}</th>
          {showIncludeColumn && <th className="text-center">{t('include')}</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const meritPercent = meritFromAmounts(
            item.referenceAmount,
            item.newAmount,
          );
          // Excluded rows are dropped on submit, so present them as greyed-out
          // and read-only — only the Include toggle stays interactive.
          const isExcluded = !item.included;
          const fieldsEditable = editable && !isExcluded;

          return (
            <tr
              key={item.id}
              className={classNames({
                'compensation-item-excluded': isExcluded,
              })}>
              <td className="fw-bold">{item.employee.name}</td>
              <td>{item.position}</td>
              <td>{t(frequencyKey(item))}</td>
              <td className="text-end">
                <FormattedCurrency
                  value={item.referenceAmount}
                  currency={currency}
                />
              </td>
              <td className="text-end">
                {fieldsEditable ? (
                  <Form.Control
                    type="number"
                    step="0.01"
                    className="compensation-merit-input ms-auto text-end"
                    value={meritPercent}
                    aria-label={t('meritPercent')}
                    onChange={(event) => {
                      // Ignore empty / non-numeric input (e.g. mid-edit when the
                      // field is cleared) so we never persist a spurious 0.
                      const raw = event.target.value;
                      if (raw === '') {
                        return;
                      }
                      const parsed = Number(raw);
                      if (Number.isNaN(parsed)) {
                        return;
                      }
                      onNewAmountChange(
                        item.id,
                        applyMeritPercent(item.referenceAmount, parsed),
                      );
                    }}
                  />
                ) : (
                  `${meritPercent.toFixed(2)}%`
                )}
              </td>
              <td className="text-end compensation-new-amount">
                {fieldsEditable ? (
                  <Form.Control
                    type="number"
                    step="0.01"
                    className="compensation-amount-input ms-auto text-end fw-bold"
                    value={item.newAmount}
                    aria-label={t('newAmount')}
                    onChange={(event) => {
                      // Ignore empty / non-numeric input (e.g. mid-edit when the
                      // field is cleared) so we never persist a spurious 0.
                      const raw = event.target.value;
                      if (raw === '') {
                        return;
                      }
                      const parsed = Number(raw);
                      if (Number.isNaN(parsed)) {
                        return;
                      }
                      onNewAmountChange(item.id, parsed);
                    }}
                  />
                ) : (
                  <span className="fw-bold">
                    <FormattedCurrency
                      value={item.newAmount}
                      currency={currency}
                    />
                  </span>
                )}
              </td>
              <td>
                <PayBandCell
                  payBand={item.payBand}
                  amount={item.newAmount}
                  currency={currency}
                />
              </td>
              {showIncludeColumn && (
                <td className="text-center compensation-include-cell">
                  <Form.Check
                    type="switch"
                    checked={item.included}
                    disabled={!editable}
                    aria-label={t('include')}
                    onChange={(event) =>
                      onIncludedChange(item.id, event.target.checked)
                    }
                  />
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};
